<template>
  <div class="animated fadeIn page-container">
    <h1>Dry Dock Summary Statistics</h1>

    <!-- filter panel -->
    <filter-panel
      class="filters-panel"
      :filter-parameters="computedFilterParameters"
      @update="applyFilterHandler"
      :notification-text="'This dashboard currently only includes dry dock visits in Japan/Korea/China'"
      :api-request-data="apiRequestData"/>

    <!-- dry dock summary charts -->
    <b-row>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Average dock stay duration (in days)"
          :dates="drydockSummary.month"
          :values="drydockSummary.averageDaysInDock"
          :y-axis="{titleText: '# days'}"
          :export-types="null"
          values-format="float1"
          @export="handleExport"/>
      </b-col>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Completed dry dock stays (per month)"
          :dates="drydockSummary.month"
          :values="drydockSummary.completedDockStays"
          :y-axis="{titleText: '# completed stays'}"
          :export-types="null"
          values-format="float1"
          @export="handleExport"/>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import debounce from 'lodash-es/debounce';
import getNested from 'lodash-es/get';
import startCase from 'lodash-es/startCase';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { downloadAsFile } from '@/shared/download-utils';
import TimeseriesChart from '../components/widgets/TimeseriesChart';
import FilterPanel from '../components/widgets/FilterPanel';
import {
  getAPIFilterValues,
  getCurrentAPIFilterValues,
  applyFilterValues,
  getFormattedFilters
} from '../components/widgets/FilterPanel';


export default {
  name: 'drydock-summary',
  components: {
    FilterPanel,
    TimeseriesChart,
  },
  data() {
    const segments = [
      'capesize',
      'panamax',
      'supramax',
      'handysize',
    ];

    let segmentChoices = [];
    segments.forEach((value) => {
      segmentChoices.push({
        value: value,
        text: startCase(value)
      });
    });

    return {
      apiRequestData: {},

      // filter parameters for internal usage
      // and for the <filter-panel> props (specific format required)
      filterSchema: {
        segment: {
          name: 'segment',
          apiName: 'segment',
          placeholder: 'Vessel segment',
          type: 'multiselect',
          value: segmentChoices[0],
          choices: segmentChoices,
          options: {
            multiple: false,
            closeOnSelect: true,
          },
        },
      },

      drydockSummary: {
        month: [],
        segment: null,
        average_days_in_dock: [],
        completed_dock_stays: [],
      },

      exportTypes: ['csv'],
    };
  },

  methods: {
    ...mapActions('api/drydock', [
      'getDrydockSummary',
    ]),

    applyFilterHandler: debounce(
      function (filterValues) {
        this.applyFilter(filterValues);
      },
      200
    ),

    async applyFilter(filterValues) {
      // convert filter-panel values to this.filterSchema
      applyFilterValues(this.filterSchema, filterValues);

      // internal filter parameters to API parameters
      let apiFilterParameters = this.getAPIFilterValues(filterValues);

      this.drydockSummary = {};
      this.getDrydockSummary(apiFilterParameters).then((response) => {
        this.drydockSummary = {
          month: [],
          averageDaysInDock: [],
          completedDockStays: []
        };

        getNested(response, 'data.drydockSummaryValues', []).forEach(item => {
          Object.keys(this.drydockSummary).forEach(fieldName => {
            this.drydockSummary[fieldName].push(item[fieldName]);
          })
        });

        this.apiRequestData = {drydockSummary: response.requestData};
      });
    },

    getAPIFilterValues(filterValues) {
      let apiFilterParameters = getAPIFilterValues(this.filterSchema, filterValues);
      if(apiFilterParameters.segment) {
        apiFilterParameters.segment = [apiFilterParameters.segment];
      }

      return apiFilterParameters;
    },

    async handleExport(format) {
      let filterParameters = {
        ...getCurrentAPIFilterValues(this.filterSchema),
        format,
      };

      let response = await this.getDrydockSummary(filterParameters);
      const timestamp = moment().format('YYYY-MM-DD-HH-mm');

      downloadAsFile(
        getNested(response, 'data', null),
        `drydock_summary_${filterParameters.segment}_export_${timestamp}.${format}`
      );
    },
  },

  computed: {
    ...mapGetters(
      'api', [
        'getActiveRequestsCount',
      ]
    ),

    ...mapGetters(
      'api/presets', ['getActivePreset']
    ),

    computedFilterParameters() {
      return getFormattedFilters(this.filterSchema);
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        // if(!this.filtersReady) {
        //   await this.loadFilters();
        //   if(!preset) {
        //     this.setDefaultValues();
        //   }
        // }

        if(preset) {
          const filterValues = getNested(preset, 'params.filterPanel', {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      },
    },
  },

};
</script>

<style scoped>
.filters-panel >>> .filter-field__select {
  min-width: 300px;
}

.filters-panel >>> .filter-field__select select {
  min-width: 150px;
}

.page-container >>> .card-content {
  min-height: 460px;
}
</style>

<style scoped lang="scss">
</style>
